import { isArray } from 'lodash-es';
import {
  ChartOptions,
  DefaultChartOptions,
  type WidgetChartOptionsMap,
} from '../constants/chartOption.defines';
import { WidgetChartType } from '@/dashboard/shared/types/widgets.types';

export type WidgetChartOptions = WidgetChartOptionsMap[keyof WidgetChartOptionsMap];

const mergeNestedOptions = <K extends WidgetChartOptions>(
  defaultOption: K,
  userOption: Record<string, any> | undefined,
): Required<K> | typeof userOption => {
  if (!defaultOption) {
    return userOption;
  }
  return {
    ...userOption,
    ...Object.keys(defaultOption).reduce((acc, key) => {
      if (
        typeof defaultOption[key] === 'object' &&
        !isArray(defaultOption[key]) &&
        userOption?.[key]
      ) {
        acc[key] = mergeNestedOptions(defaultOption[key], userOption[key]);
      } else if (
        isArray(defaultOption[key]) &&
        typeof defaultOption[key][0] === 'object' &&
        userOption?.[key]
      ) {
        const baseArray =
          userOption[key].length >= defaultOption[key].length
            ? userOption[key]
            : defaultOption[key];
        acc[key] = baseArray?.map((option: Record<string, any>) =>
          mergeNestedOptions(defaultOption[key][0], option),
        );
      } else {
        acc[key] = userOption?.[key] ?? defaultOption[key];
      }
      return acc;
    }, {}),
  };
};

const applyClickableInfo = (chartOption?: Record<string, any>) => {
  const { columns, displayColumns } = chartOption || {};

  columns?.forEach((column) => {
    column.clickable = column.clickable ?? false;
  });

  displayColumns?.forEach((column) => {
    if (column.meta) {
      column.meta.clickable = column.meta.clickable ?? false;
    }
  });

  return chartOption;
};

/**
 * 차트 타입에 맞는 기본 차트 옵션 적용
 * @param chartType - 차트 타입
 * @param chartOption - 차트 옵션
 */
export const applyDefaultChartOptions = <K extends WidgetChartType>(
  chartType: K,
  chartOption: Record<string, any> | undefined,
): Required<ChartOptions<K>> | typeof chartOption => {
  const defaultChartOption: ChartOptions<K> = DefaultChartOptions[chartType];

  switch (chartType) {
    case 'TREE_GRID': {
      return applyClickableInfo(mergeNestedOptions(defaultChartOption, chartOption));
    }
    case 'TIME_SERIES':
    case 'SCATTER':
    case 'ONE_DAY_COMPARE':
    case 'EQUALIZER':
    case 'SCOREBOARD':
    case 'PIE':
    case 'STACKED_PROPORTION_BAR':
    case 'GAUGE':
    case 'HEATMAP':
    case 'TABLE':
    case 'TOP_LIST':
    case 'STATUS':
    case 'STATUS_SUMMARY':
    case 'TOPOLOGY':
    case 'SERVER':
    case 'ACTION_VIEW':
    case 'EXCLUSIVE':
    case 'SERVICE_CARD':
    case 'MEMO':
    case 'TEXT':
    case 'IMAGE':
    case 'HORIZONTAL_FILTER':
    case 'VERTICAL_FILTER':
    case 'GROUP':
    case 'CLUSTER':
    case 'REALTIME':
    case 'BY_PERIOD':
    case 'TAB_GROUP': {
      return mergeNestedOptions(defaultChartOption, chartOption);
    }
    default:
      throw new Error(`Not supported chart type: ${chartType}`);
  }
};
